/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {cellLabelStyles} from '@ion/dls/src/grid/ColoredStatusCell';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {dlsColor} from '@ion/dls/src/theme/theme';

const getStatusColors = () => ({
  completed : {
    textColor : dlsColor('caGreen'),
    color     : 'rgba(105, 178, 70, 0.1)'
  },
  failed : {
    textColor : dlsColor('caRed'),
    color     : 'rgba(226, 35, 26, 0.1)'
  },
  wip : {
    textColor : dlsColor('caOrange'),
    color     : 'rgba(79, 79, 79, 0.1)'
  },
  cancelled : {
    textColor : dlsColor('grey2'),
    color     : 'rgba(79, 79, 79, 0.1)'
  }
});

const useStyles = makeStyles(() => ({
  labelStyle : {
    color           : ({textColor}: { textColor: string, color: string }) => textColor || '',
    backgroundColor : ({color}: { textColor: string, color: string }) => color || '',
    lineHeight      : '23px',
    width           : '65%',
    display         : 'flex',
    justifyContent  : 'center',
    padding         : '0px 8px 0px 8px',
    borderRadius    : '2px'
  }
}));

export const failedStatus = {
  FAILED      : 'Failed - Failed',
  MAX_RETRIES : 'Failed - Max_Retries',
  UNKNOWN     : 'Unknown'
};
export const cancelledStatus = {
  CANCELLED : 'Cancelled'
};
export const completedStatus = {
  DISCOVERED : 'Completed - Discovered',
  REMOVED    : 'Completed - Removed',
  SUCCESS    : 'Completed - Success'
};
export const wipStatus = {
  PENDING     : 'WIP - Pending',
  THROTTLED   : 'WIP - Throttled',
  IN_PROGRESS : 'WIP - In_Progress',
  RETRY       : 'WIP - Retry'
};

export const getStatus = (status: string) => {
  const statusColors = getStatusColors();
  if (!isUndefined(failedStatus[status])) {
    return statusColors.failed;
  }
  if (!isUndefined(completedStatus[status])) {
    return statusColors.completed;
  }
  if (!isUndefined(wipStatus[status])) {
    return statusColors.wip;
  }
  return statusColors.cancelled;
};

/**
 * The given status must also correspond to a given valid or invalid status that consumers
 * can customize. Please see usage examples below for further details.
 *
 * ```typescript
 *
 *    import {FileHistoryStatusCell} from '@ion/dls/src/grid/';
 *
 *    <FileHistoryStatusCell status={status} />
 *
 * ```
 */
export const FileHistoryStatusCell = ({data, isCurrentView = false}: GenericObject) => {
  let pendingStatus;
  data?.pending?.forEach(statusVal => {
    pendingStatus = statusVal?.jobStatus;
  });
  const status  = data?.statusUI || data?.pendingJobStatusUI || pendingStatus;
  const styles  = useStyles({...getStatus(status)});
  const classes = useExtendStyles(() => styles, cellLabelStyles)();
  if (status) {
    return (
      <div className={classes.labelContainer}>
        <div className={styles.labelStyle}>{isCurrentView ? status : translate(`admin-devices:view.files.statusText.${status?.toLowerCase()}`)}</div>
      </div>
    );
  }
  return <div/>;
};

export default memo(FileHistoryStatusCell);
